/**
 * BwfSessionRecorder — streaming BWF capture for RT remote sessions.
 *
 * Unlike the take-based `Recorder` (recorder/recorder.ts) which buffers PCM in
 * memory, this recorder writes 24-bit BWF to disk *while recording*:
 *
 *   mic → AudioWorklet ('ohear-recorder', 128-frame Float32 quanta)
 *       → Pcm24BlockWriter (pack + aggregate ~144 KB ≈ 1 s)
 *       → RecordingSink (user dir / OPFS / memory)
 *
 * Every 3 s the RIFF and data chunk sizes are patched in place, so a crashed
 * session leaves a parseable WAV missing at most the last few seconds
 * (durable in OPFS sync-access mode; see sink.ts for the user-dir caveat).
 *
 * The worklet module ('ohear-recorder' from worklets/recorder.worklet.ts) is
 * reused as-is — pass `workletUrl` in the constructor options, or add the
 * module to the AudioContext yourself before calling start().
 */

import type { RecorderFrameMessage } from '../worklets/recorder.worklet'
import {
  BWF_BYTES_PER_SAMPLE,
  BWF_SAMPLE_RATE,
  patchSizes,
  writeBwfHeader,
} from './bwf'
import { Pcm24BlockWriter } from './pack24'
import type { RecordingSink } from './sink'
import { OpfsSink, UserDirSink } from './sink'
import type { RtSidecarDropout, RtSidecarMtcEntry, RtSidecarNetworkSample } from './sidecar'
import { buildSidecar } from './sidecar'

/** ~1 s of 48 kHz mono 24-bit PCM per sink write. Multiple of 384 (one quantum). */
const BLOCK_BYTES = BWF_SAMPLE_RATE * BWF_BYTES_PER_SAMPLE // 144 000
/** Header size-field write-back cadence. */
const PATCH_INTERVAL_MS = 3000

export interface BwfSessionRecorderOptions {
  context: AudioContext
  source: AudioNode
  /**
   * URL of the compiled recorder worklet module. If omitted, the module must
   * already have been added to `context.audioWorklet` by the app.
   */
  workletUrl?: string | URL
}

export interface BwfSessionStartOptions {
  /** File base WITHOUT extension, e.g. `ProjectX_Actor_T03_TC01003000`. */
  fileBase: string
  /** bext TimeReference: samples since midnight @48 kHz. */
  tcStartSamples: number
  /** bext Originator (≤32 ASCII). */
  originator: string
  /** bext OriginatorReference (≤32 ASCII). */
  originatorRef: string
  /** bext Description (≤256 ASCII). */
  description: string
}

export interface BwfSessionResult {
  fileName: string
  /** Total file size in bytes (header + PCM data + RIFF pad if any). */
  bytes: number
  durationSec: number
  getBlob(): Promise<Blob>
}

export interface BwfSessionSupport {
  worklet: boolean
  fsAccess: boolean
  opfs: boolean
}

type State = 'idle' | 'recording' | 'stopping'

export class BwfSessionRecorder {
  /** Extra telemetry merged into the .json sidecar on stop(). */
  readonly mtcLog: RtSidecarMtcEntry[] = []
  readonly networkSamples: RtSidecarNetworkSample[] = []
  readonly dropouts: RtSidecarDropout[] = []
  deviceLabel: string | null = null

  onLevel?: (peakDbfs: number) => void
  onBytes?: (written: number) => void

  private readonly context: AudioContext
  private readonly source: AudioNode
  private readonly workletUrl?: string | URL

  private sink?: RecordingSink
  private workletNode?: AudioWorkletNode
  private workletModuleAdded = false
  private state: State = 'idle'

  private headerBytes = 0
  private dataBytes = 0
  private samplesWritten = 0
  private blockWriter?: Pcm24BlockWriter
  private patchTimer?: ReturnType<typeof setInterval>
  private startOpts?: BwfSessionStartOptions

  /**
   * Sink-op serializer: appends and patches are queued so positional writes
   * never interleave out of order.
   */
  private queue: Promise<void> = Promise.resolve()
  private queueError: Error | null = null

  constructor(opts: BwfSessionRecorderOptions & { workletUrl?: string | URL }) {
    this.context = opts.context
    this.source = opts.source
    this.workletUrl = opts.workletUrl
  }

  /** Feature detection for the pre-flight screen. */
  static support(): BwfSessionSupport {
    const g = globalThis as Record<string, unknown>
    return {
      worklet: typeof g['AudioWorkletNode'] !== 'undefined',
      fsAccess: typeof g['showDirectoryPicker'] === 'function',
      opfs:
        typeof navigator !== 'undefined' &&
        typeof navigator.storage?.getDirectory === 'function',
    }
  }

  /**
   * Choose where recordings land. With a user-picked directory handle →
   * UserDirSink ('user-dir'); without → OPFS fallback ('opfs').
   */
  async useDirectory(handle?: FileSystemDirectoryHandle): Promise<'user-dir' | 'opfs'> {
    if (this.state !== 'idle') {
      throw new Error(`BwfSessionRecorder.useDirectory: invalid state ${this.state}`)
    }
    if (handle) {
      this.sink = new UserDirSink(handle)
      return 'user-dir'
    }
    if (!BwfSessionRecorder.support().opfs) {
      throw new Error('BwfSessionRecorder.useDirectory: no directory handle and OPFS unavailable')
    }
    this.sink = new OpfsSink()
    return 'opfs'
  }

  /** Begin a take: open `<fileBase>.wav`, write the BWF header, start capture. */
  async start(opts: BwfSessionStartOptions): Promise<void> {
    if (this.state !== 'idle') {
      throw new Error(`BwfSessionRecorder.start: invalid state ${this.state}`)
    }
    if (!this.sink) {
      // Default to OPFS when the app never called useDirectory().
      await this.useDirectory()
    }
    const sink = this.sink
    if (!sink) throw new Error('BwfSessionRecorder.start: no sink')

    this.startOpts = opts
    this.dataBytes = 0
    this.samplesWritten = 0
    this.queue = Promise.resolve()
    this.queueError = null

    const header = writeBwfHeader({
      tcStartSamples: opts.tcStartSamples,
      originator: opts.originator,
      originatorRef: opts.originatorRef,
      description: opts.description,
    })
    this.headerBytes = header.length

    await sink.open(`${opts.fileBase}.wav`)
    await sink.append(header)

    this.blockWriter = new Pcm24BlockWriter(BLOCK_BYTES, (block) => {
      this.enqueue(async () => {
        await sink.append(block)
        this.dataBytes += block.length
        this.onBytes?.(this.dataBytes)
      })
    })

    await this.attachWorklet()

    this.patchTimer = setInterval(() => {
      this.flushAndPatch()
    }, PATCH_INTERVAL_MS)

    this.state = 'recording'
  }

  /** Stop, flush, finalize sizes, write the sidecar, close the sink. */
  async stop(): Promise<BwfSessionResult> {
    if (this.state !== 'recording') {
      throw new Error(`BwfSessionRecorder.stop: invalid state ${this.state}`)
    }
    this.state = 'stopping'

    if (this.patchTimer) {
      clearInterval(this.patchTimer)
      this.patchTimer = undefined
    }
    this.detachWorklet()

    const sink = this.sink
    const opts = this.startOpts
    if (!sink || !opts || !this.blockWriter) {
      throw new Error('BwfSessionRecorder.stop: inconsistent internal state')
    }

    // Final flush of the partial block, then pad + size write-back + sidecar.
    this.blockWriter.flush()
    this.enqueue(async () => {
      let fileBytes = this.headerBytes + this.dataBytes
      if (this.dataBytes % 2 === 1) {
        // RIFF pad byte for odd-sized data chunk (not counted in data size).
        await sink.append(new Uint8Array(1))
        fileBytes += 1
      }
      const { riff, data } = patchSizes(this.headerBytes, this.dataBytes)
      await sink.patch(riff.offset, riff.bytes)
      await sink.patch(data.offset, data.bytes)

      const sidecar = buildSidecar({
        fileBase: opts.fileBase,
        tcStartSamples: opts.tcStartSamples,
        sampleRateActual: this.context.sampleRate || BWF_SAMPLE_RATE,
        durationSec: this.samplesWritten / BWF_SAMPLE_RATE,
        dataBytes: this.dataBytes,
        deviceLabel: this.deviceLabel,
        mtcLog: this.mtcLog,
        dropouts: this.dropouts,
        networkSamples: this.networkSamples,
      })
      await sink.writeSidecar(`${opts.fileBase}.json`, JSON.stringify(sidecar, null, 2))
      await sink.close()
    })
    await this.drainQueue()

    const fileName = `${opts.fileBase}.wav`
    const durationSec = this.samplesWritten / BWF_SAMPLE_RATE
    const bytes = this.headerBytes + this.dataBytes + (this.dataBytes % 2)
    const result: BwfSessionResult = {
      fileName,
      bytes,
      durationSec,
      getBlob: () => sink.getBlob(),
    }

    this.state = 'idle'
    this.blockWriter = undefined
    return result
  }

  /**
   * TEST HOOK — feed Float32 PCM straight into the write path, bypassing the
   * AudioWorklet (which doesn't exist under vitest). Mirrors exactly what the
   * worklet message handler does.
   */
  _ingestForTest(frames: Float32Array): void {
    this.ingest(frames, framePeak(frames))
  }

  /** TEST HOOK — inject a sink (e.g. MemorySink) without touching FS APIs. */
  _useSinkForTest(sink: RecordingSink): void {
    if (this.state !== 'idle') {
      throw new Error('BwfSessionRecorder._useSinkForTest: only valid while idle')
    }
    this.sink = sink
  }

  /** TEST HOOK — run the 3 s flush+patch cycle synchronously on demand. */
  async _patchForTest(): Promise<void> {
    this.flushAndPatch()
    await this.drainQueue()
  }

  // ---------- internals ----------

  private ingest(frames: Float32Array, peak: number): void {
    if (this.state !== 'recording' || !this.blockWriter) return
    this.samplesWritten += frames.length
    this.blockWriter.push(frames)
    if (this.onLevel) {
      this.onLevel(peak > 0 ? 20 * Math.log10(peak) : -Infinity)
    }
  }

  private flushAndPatch(): void {
    const sink = this.sink
    const writer = this.blockWriter
    if (!sink || !writer) return
    // Flush the partial block first so the patched sizes cover everything
    // captured so far — this is what makes a crash recoverable.
    writer.flush()
    this.enqueue(async () => {
      const { riff, data } = patchSizes(this.headerBytes, this.dataBytes)
      await sink.patch(riff.offset, riff.bytes)
      await sink.patch(data.offset, data.bytes)
      await sink.sync()
    })
  }

  private async attachWorklet(): Promise<void> {
    if (typeof AudioWorkletNode === 'undefined') {
      // Test/headless environment without Web Audio — _ingestForTest drives us.
      return
    }
    if (this.workletUrl && !this.workletModuleAdded) {
      await this.context.audioWorklet.addModule(this.workletUrl.toString())
      this.workletModuleAdded = true
    }
    this.workletNode = new AudioWorkletNode(this.context, 'ohear-recorder')
    this.workletNode.port.onmessage = (e: MessageEvent<RecorderFrameMessage>) => {
      if (e.data.type === 'frames') this.ingest(e.data.frames, e.data.peak)
    }
    this.source.connect(this.workletNode)
    // Deliberately not connected to destination — no local monitoring.
  }

  private detachWorklet(): void {
    if (!this.workletNode) return
    try {
      this.source.disconnect(this.workletNode)
    } catch {
      /* already disconnected */
    }
    this.workletNode.port.onmessage = null
    this.workletNode.disconnect()
    this.workletNode = undefined
  }

  private enqueue(fn: () => Promise<void>): void {
    this.queue = this.queue.then(fn).catch((err: unknown) => {
      if (!this.queueError) {
        this.queueError = err instanceof Error ? err : new Error(String(err))
      }
    })
  }

  private async drainQueue(): Promise<void> {
    await this.queue
    if (this.queueError) {
      const err = this.queueError
      this.queueError = null
      throw err
    }
  }
}

function framePeak(frames: Float32Array): number {
  let peak = 0
  for (let i = 0; i < frames.length; i++) {
    const s = frames[i] ?? 0
    const a = s < 0 ? -s : s
    if (a > peak) peak = a
  }
  return peak
}
